/* eslint-disable no-restricted-syntax */
/* eslint-disable no-await-in-loop */
import { setIABudget } from "Actions/IABudget";
import { useCartAmount } from "Components/ServiceForm/ProductsResultSearch/useCartAmount";
import { useBudgetContext } from "Hooks/useBudgetContext";
import { Product } from "Hooks/useProducts/interface";
import { IAResponse } from "Pages/PreBudgetConvertionIA/interface";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { addGeneralInfoAndDocuments } from "Requests/Budget";
import { processMedicalClaimWithIA } from "Requests/IA/processMedicalClaim";
import { getProducts as getProductsByIds } from "Requests/Products";
import { FakePromise } from "Utils/Promise";
import { createToast } from "Utils/toastFunc";
import { useIATracking } from "./useIATracking";

type ErrorAddProduct = {
  product: string;
  error: string;
};

export const useCreateIABudget = () => {
  const {
    productsCategoriesSelected: productsCategories,
    budget,
    setDocuments,
  } = useBudgetContext();
  const [modalProductsFailed, setModalProductsFailed] = useState(false);
  const [openModalIA, setOpenModalIA] = useState(false);
  const [runningIA, setRunningIA] = useState(false);
  const [failedProductsOnAdd, setFailedProductsonAdd] = useState<
    { error: string; product: string }[]
  >([]);
  const [isAddingProducts, setIsAddingProducts] = useState(false);

  const dispatch = useDispatch();

  const { initializeNewTracking } = useIATracking();

  const productListWithIds = new Set();
  const productListWithNoIds = new Set<ErrorAddProduct>();

  const {
    handleAddProduct,
    handleRemoveToCart,
    cancelAddProduct,
    proceedAddProduct,
    handleConfirmPartnerSelection,
    handleChangePartner,
    closeModal,
    modal,
    partnersOptions,
    preparation,
  } = useCartAmount();

  const categoryIds = productsCategories
    ?.map((category) => String(category))
    .join(",");

  const gerProducts = async (productsIds: number[]) => {
    return await getProductsByIds({
      page: 1,
      filters: {
        product_ids: productsIds as any,
        categories: categoryIds,
        channel_id: 2,
      },
    });
  };

  const createFirstProductBudget = async (products: Product[]) => {
    let productsList = [...products];
    let budgetId = budget?.id || 0;
    const failedAddedProducts = [];

    for (const product of products) {
      if (budgetId) {
        break;
      }

      const response = await handleAddProduct(product, undefined, true);

      if (response.id) {
        budgetId = response.id;
        productsList = productsList.slice(1);
      }

      if (response?.response?.data?.errors[0]) {
        failedAddedProducts.push({
          product: product.product,
          error: response?.response?.data?.errors[0],
        });
      }
    }

    return { productsList, budgetId, failedAddedProducts };
  };

  const loopRemainProductsIds = async (
    products: Product[],
    budgetId: number
  ) => {
    const listFailedProducts = [];

    for (const product of products) {
      await FakePromise(500);

      const response = await handleAddProduct(product, budgetId, true);

      if (response?.response?.data?.errors[0]) {
        listFailedProducts.push({
          product: product.product,
          error: response?.response?.data?.errors[0].message as string,
        });
      }
    }

    return { listFailedProducts };
  };

  const getProductsFromIds = async () => {
    const spreadIds: number[] = [...productListWithIds] as number[];

    const response = await gerProducts(spreadIds);

    return {
      products: response.data,
    };
  };

  const orderProductsByIAFounded = (response: IAResponse, products: any) => {
    const orderedProducts = [] as Product[];

    productListWithIds.forEach((item) => {
      const productFound = products.find(
        (product: Product) => product.id === item
      );
      if (productFound) orderedProducts.push(productFound);
    });

    return orderedProducts;
  };

  const filterProductsWithID = async (response: IAResponse) => {
    response.matching_results.resultados.forEach((product) => {
      if (product.id !== null && !productListWithIds.has(product.id)) {
        return productListWithIds.add(product.id);
      }
      productListWithNoIds.add({
        product: product.nome_original,
        error: "Produto não encontrado",
      });
    });
  };

  const addProductsWithIA = async (response: IAResponse) => {
    filterProductsWithID(response);

    const { products } = await getProductsFromIds();

    const orderedProducts = orderProductsByIAFounded(response, products);

    const { budgetId, productsList, failedAddedProducts } =
      await createFirstProductBudget(orderedProducts);

    const { listFailedProducts } = await loopRemainProductsIds(
      productsList,
      budgetId
    );

    setIsAddingProducts(false);

    return {
      budgetId,
      productsNotAdded: [
        ...failedAddedProducts,
        ...listFailedProducts,
        ...productListWithNoIds,
      ],
    };
  };

  function blobToBase64(blob: File) {
    return new Promise<{ base64: string; extension: string }>(
      (resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result as string;
          const base64 = result.split(",")[1];

          let extension = "";
          if (blob.type) {
            const mimeParts = blob.type.split("/");
            extension = mimeParts[1];
          }

          resolve({ base64, extension });
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      }
    );
  }

  const onSelectIAFiles = async (files: File[]) => {
    try {
      const response = await processMedicalClaimWithIA(files);

      dispatch(setIABudget(response));

      const { matching_results } = response;

      if (matching_results.resultados.length === 0) {
        const res = files.map((file) => blobToBase64(file));

        const promises = await Promise.all(res);

        const docs = promises.map((fil) => ({
          base64: `${fil.base64}`,
          file_path: `.${fil.extension}`,
          name: "doc",
        }));

        setDocuments(docs);

        throw new Error("Nenhum produto foi encontrado pela IA");
      }

      return {
        response,
      };
    } catch (error: any) {
      if (error.response.data.message.includes("PDF com muitas")) {
        return createToast("error", "PDF com muitas páginas");
      }
      if (error.message) {
        return createToast("error", error.message);
      }

      return createToast(
        "error",
        "Ocorreu um erro ao tentar fazer o upload dos arquivos, tente novamente !"
      );
    }
  };

  const attachMedicalOrderToBudget = async (
    files: File[],
    budgetId: number
  ) => {
    try {
      const formData = new FormData();

      files.forEach((file) => {
        formData.append("documents[]", file);
      });

      const response = await addGeneralInfoAndDocuments(budgetId, formData);
      const doc = response?.data.documents_base64;
      setDocuments([...doc]);
      createToast("success", "Arquivo anexado com sucesso !");
    } catch (error) {
      throw new Error(
        "Não foi possível anexar os arquivos ao orçamento, tente novamente !"
      );
    }
  };

  const handleAddProductsFromFile = async (files: File[]) => {
    try {
      setRunningIA(true);
      setOpenModalIA(false);

      const response = await onSelectIAFiles(files);

      if (
        response &&
        response.response.matching_results.resultados.length >= 1
      ) {
        initializeNewTracking(response.response);

        setRunningIA(false);

        setIsAddingProducts(true);
        const { productsNotAdded, budgetId } = await addProductsWithIA(
          response.response
        );

        await attachMedicalOrderToBudget(files, budgetId);

        if (productsNotAdded.length) {
          setFailedProductsonAdd(productsNotAdded);
          await FakePromise(500);
          setModalProductsFailed(true);
        }
      }
      setRunningIA(false);
    } catch (error: any) {
      console.log({ error });
      if (error.message) createToast("error", error.message);
    } finally {
      setIsAddingProducts(false);
      setOpenModalIA(false);
    }
  };

  return {
    cancelAddProduct,
    proceedAddProduct,
    handleConfirmPartnerSelection,
    handleChangePartner,
    closeModal,
    modal,
    partnersOptions,
    preparation,
    modalProductsFailed,
    openModalIA,
    runningIA,
    setOpenModalIA,
    failedProductsOnAdd,
    setModalProductsFailed,
    handleAddProductsFromFile,
    isAddingProducts,
    initializeNewTracking,
  };
};
